// 表格列与展示辅助：日期格式解析、首列/指标/维度列生成（区分下载/图表模式），覆盖维度显示值
import { IMetricInfo } from '../../AIContent/hooks';
import { IColumn , IAdditionalTableField } from '../../AIContent/_type';
import {
  DATE_FORMAT_MAP,
  DATE_OR_TIME_DATA_TYPE,
  EXCEL_TABLE_FORMAT_MAP,
  TABLE_FORMAT_MAP,
} from '../../../../config/constant';
import { getDimensionName } from '../../AIContent/_utils';
import { getFormatRule } from '@/pages/aigc/utils/utils';


export function resolveDateFormatter(granularity?: string, fallback = 'day'): string {
  return DATE_FORMAT_MAP?.[granularity as keyof typeof DATE_FORMAT_MAP] || fallback;
}

type TMode = 'download' | 'chart';

type TDimensionLike = {
  data_key?: string;
  name?: string;
  tableFormat?: string;
  format?: string;
  value?: any[];
};

export const buildFirstDimensionColumn = (
  xAxis: any[],
  dimensionInfo: TDimensionLike[],
  granularity?: string,
  mode: TMode = 'download'
): IColumn => {
  const firstColumnsData = dimensionInfo?.find(
    (item) => item?.data_key === (xAxis?.[0]?.date_key || xAxis?.[0])
  );
  const firstColumnsDataKey = firstColumnsData?.data_key || '';
  const firstColumnsName = getDimensionName(firstColumnsDataKey, dimensionInfo as any);
  let format = (firstColumnsData as any)?.tableFormat || (firstColumnsData as any)?.format;
  const dateFormatter = resolveDateFormatter(granularity);
  if (DATE_OR_TIME_DATA_TYPE?.includes(firstColumnsDataKey as string) && !format) {
    format = dateFormatter;
  }

  let columnDateType: string | undefined;
  if (mode === 'download') {
    columnDateType = DATE_OR_TIME_DATA_TYPE.includes(firstColumnsDataKey as string)
      ? '@'
      : EXCEL_TABLE_FORMAT_MAP?.[format as keyof typeof EXCEL_TABLE_FORMAT_MAP] || '@';
  } else {
    columnDateType = getFormatRule(format || '') || (firstColumnsDataKey === 'date' ? dateFormatter : 'string');
  }

  return {
    name: firstColumnsName,
    name_en: firstColumnsName,
    origin_name: firstColumnsDataKey,
    display_name: `${firstColumnsDataKey}_display`,
    isDimension: true,
    can_sort: false,
    type: 0,
    column_date_type: columnDateType,
    isDate: firstColumnsDataKey === 'date',
    accuracy: null,
    table_code: '',
  };
};

export const buildMetricColumns = (
  metricsInfo: IMetricInfo[],
  metricKey: string,
  getDisplayName: (metricDataKey: string) => string,
  mode: TMode = 'download'
): IColumn[] => {
  const list = metricsInfo?.filter((item) => item?.data_key === metricKey);
  return (
    list?.map((metricItem) => {
      const metricDataKey = (metricItem?.data_key || '') as string;
      const metricType = (metricItem as any)?.type || '';
      const metricFormat = getFormatRule((metricItem as any)?.format);

      const baseColumnType =
        metricFormat || TABLE_FORMAT_MAP?.[metricType as keyof typeof TABLE_FORMAT_MAP];
      const excelColumnType =
        baseColumnType && EXCEL_TABLE_FORMAT_MAP?.[baseColumnType as keyof typeof EXCEL_TABLE_FORMAT_MAP];
      const columnDateType =
        mode === 'download'
          ? excelColumnType || baseColumnType || '@'
          : baseColumnType;

      const metricName = metricItem?.name || getDisplayName(metricDataKey);
      return {
        name: metricName,
        name_en: metricDataKey,
        origin_name: metricDataKey,
        can_sort: true,
        type: 1,
        column_date_type: columnDateType || '@',
        origin_type: metricType,
        isDate: false,
        accuracy: null,
        table_code: '',
      };
    }) || []
  );
};

export const buildDimensionColumns = (
  dimensions: TDimensionLike[],
  xAxis: any[] | undefined,
  granularity: string,
  dimensionInfo?: TDimensionLike[],
  mode: TMode = 'download'
): IColumn[] => {
  const dateFormatter = resolveDateFormatter(granularity);
  return (
    dimensions
      ?.filter((item) => item?.data_key !== (xAxis?.[0]?.date_key || xAxis?.[0]))
      ?.map((item) => {
        const key = item?.data_key;
        const dimensionName = dimensionInfo
          ? getDimensionName(key as string, dimensionInfo as any)
          : (item?.name as string) || (key as string);
        let columnDateType = 'string';
        if (mode === 'download' && DATE_OR_TIME_DATA_TYPE.includes((key || '') as string)) {
          columnDateType = '@';
        } else if (key === 'date') {
          columnDateType = dateFormatter;
        }
        return {
          name: dimensionName,
          name_en: dimensionName,
          origin_name: key || '',
          display_name: `${key}_display`,
          isDimension: true,
          can_sort: false,
          type: 0,
          column_date_type: columnDateType,
          accuracy: null,
          table_code: '',
        };
      }) || []
  );
};

export const applyDisplayToDimensions = (data: any[], columns: IColumn[]): any[] =>
  data?.map((item) => {
    const processedItem = { ...item };
    columns?.forEach((column) => {
      if (column?.isDimension && column?.display_name) {
        const displayValue = item[column?.display_name];
        if (displayValue !== undefined && displayValue !== null) {
          processedItem[column?.origin_name] = displayValue;
        }
      }
    });
    return processedItem;
  });

export const buildAdditionalColumns = (
  additionalFields: IAdditionalTableField[] | undefined,
  mode: TMode = 'download'
): IColumn[] => {
  if (!additionalFields || !Array.isArray(additionalFields)) {
    return [];
  }

  return additionalFields.map((field) => {
    const fieldFormat = field.format || 'string';
    const baseType = getFormatRule(fieldFormat) || fieldFormat;
    const columnDateType =
      mode === 'download'
        ? EXCEL_TABLE_FORMAT_MAP?.[baseType as keyof typeof EXCEL_TABLE_FORMAT_MAP] || baseType || '@'
        : baseType;

    return {
      name: field.name,
      name_en: field.name,
      origin_name: field.data_key,
      display_name: field.data_key,
      isDimension: true,
      can_sort: false,
      type: 0,
      column_date_type: columnDateType,
      isDate: DATE_OR_TIME_DATA_TYPE.includes(field.data_key),
      accuracy: null,
      table_code: '',
    };
  });
};

